
// src/features/wear-parts/toolItemsByPage.ts
import { WEAR_PARTS_BASE, withSerialQuery, withSerialTemplate } from "./wearPartsConfig";
import type { ToolPageKey } from "./typeToToolPage";

export type WearItem = {
  id: string;                // part number or unique id
  title: string;
  description?: string;
  imageSrc: string;
  buyUrl?: string;           // we'll append ?serial= with helper
  buyUrlTemplate?: string;   // optional alternative
};

export type ToolPage = {
  id: ToolPageKey;
  title: string;
  subtitle?: string;
  items: WearItem[];
};

/** Fallback image to use when "No Picture" is provided */
const PLACEHOLDER_IMG = "/assets/wear/no-image.png";

/** Ensure we have an https URL; map "No Picture" to a placeholder */
function img(u?: string): string {
  if (!u || u.toLowerCase() === "no picture") return PLACEHOLDER_IMG;
  return u.startsWith("http") ? u : `https://${u}`;
}

export const TOOL_ITEMS_BY_PAGE: Record<ToolPageKey, ToolPage> = {
  // ---------- 1) FGT ----------
  FGT: {
    id: "FGT",
    title: "FGT Wear Parts & Accessories",
    subtitle: "Easy online ordering. Fast delivery. More time for in the grind.",
    items: [
      {
        id: "320-15-012",
        title: "Double Carbine",
        description: "Standard Duty",
        imageSrc: img("https://shop.fecon.com/cdn/shop/files/320-15-012_ec_4731ba4f-3d19-40e1-81d7-0c9b5eb81068.png?v=1728467962"),
        buyUrl: `${WEAR_PARTS_BASE}/products/tool-fgt-standard-duty-wide`,
      },
      {
        id: "320-15-011",
        title: "High Abrasion",
        description: "Severe Duty",
        imageSrc: img("shop.fecon.com/cdn/shop/files/320-15-011_ec_7a93af2d-2e0a-4734-9b31-b9c2fa85563e.png"),
        buyUrl: `${WEAR_PARTS_BASE}/products/tool-fgt-severe-duty-wide`,
      },
      {
        id: "320-15-013",
        title: "Stone Tool",
        description: "Severe Duty",
        imageSrc: img("https://shop.fecon.com/cdn/shop/files/320-15-013_ec_7_dbae9e91-f4b0-45dd-88ad-7cbba3a760c6.png?v=1728467975"),
        buyUrl: `${WEAR_PARTS_BASE}/products/tool-fgt-stone`,
      },
      {
        id: "320-14-212",
        title: "Viking Sword",
        description: "High Efficiency",
        imageSrc: img("https://shop.fecon.com/cdn/shop/files/320-14-212.png?v=1731429616"),
        buyUrl: `${WEAR_PARTS_BASE}/products/viking-sword-knife-tool`,
      },
    ],
  },

  // ---------- 2) DCR ----------
  DCR: {
    id: "DCR",
    title: "DCR Wear Parts & Accessories",
    subtitle: "Easy online ordering. Fast delivery. More time for in the grind.",
    items: [
      {
        id: "320-14-213",
        title: "Viking Axe",
        description: "Standard Duty",
        imageSrc: img("https://shop.fecon.com/cdn/shop/files/320-14-213.png?v=1731429599"),
        buyUrl: `${WEAR_PARTS_BASE}/products/viking-axe-knife-tool`,
      },
    ],
  },

  // ---------- 3) FMX ----------
  FMX: {
    id: "FMX",
    title: "Wear Parts & Accessories",
    subtitle: "Easy online ordering. Fast delivery. More time for in the grind.",
    items: [
      {
        id: "501-14-013",
        title: "Cubit Knife",
        description: "Standard Duty",
        imageSrc: img("https://shop.fecon.com/cdn/shop/files/501-14-013_ec_f3423ebf-fdac-4f53-a9d7-8712215c2eba.png?v=1728475728"),
        buyUrl: `${WEAR_PARTS_BASE}/products/cubit-knife-tool-501-14-013`,
      },
      {
        id: "501-15-006",
        title: "Cubit Carbide",
        description: "Extended Life",
        imageSrc: img("https://shop.fecon.com/cdn/shop/files/501-15-006_ec_6ee9c79a-2392-4ca1-8e25-758d010f1d27.png?v=1728610541"),
        buyUrl: `${WEAR_PARTS_BASE}/products/tool-cubit-standard-duty-carbide`,
      },
    ],
  },

  // ---------- 4) FDM ----------
  FDM: {
    id: "FDM",
    title: "Wear Parts & Accessories",
    subtitle: "Easy online ordering. Fast delivery. More time for in the grind.",
    items: [
      {
        id: "802-14-002",
        title: "Deck Blade",
        description: "Standard Duty",
        imageSrc: img("No Picture"),
        buyUrl: `${WEAR_PARTS_BASE}/products/blade-deck-mower`,
      },
    ],
  },

  // ---------- 5) FDX ----------
  FDX: {
    id: "FDX",
    title: "Wear Parts & Accessories",
    subtitle: "Easy online ordering. Fast delivery. More time for in the grind.",
    items: [
      {
        id: "802-65-001",
        title: "Cutting Tooth",
        description: "Standard Duty",
        imageSrc: img("No Picture"),
        buyUrl: `${WEAR_PARTS_BASE}/products/cutting-tooth-beaver-2-shanked`,
      },
    ],
  },

  // ---------- 6) SHX ----------
  SHX: {
    id: "SHX",
    title: "Wear Parts & Accessories",
    subtitle: "Easy online ordering. Fast delivery. More time for in the grind.",
    items: [
      {
        id: "800-75-026",
        title: "Cone",
        description: "Drill Tip",
        imageSrc: img("https://shop.fecon.com/cdn/shop/files/800-75-026_55ae460f-534e-4cd5-b2e2-f5eb2ee8b89e.jpg?v=1728527055"),
        buyUrl: `${WEAR_PARTS_BASE}/products/cone-mach-3-4-pitch-spline-1-bolt`,
      },
    ],
  },

  // ---------- 7) SHXCUL ----------
  SHXCUL: {
    id: "SHXCUL",
    title: "Wear Parts & Accessories",
    subtitle: "Easy online ordering. Fast delivery. More time for in the grind.",
    items: [
      {
        id: "803-23-008",
        title: "Cutting Bit",
        description: "Standard Duty",
        imageSrc: img("https://fecon.com/wp-content/uploads/2023/04/Cutting-Bit.png"),
        buyUrl: `${WEAR_PARTS_BASE}/products/welded-2-blade-compact-stumpex`,
      },
    ],
  },

  // ---------- 8) SC ----------
  SC: {
    id: "SC",
    title: "Wear Parts & Accessories",
    subtitle: "Easy online ordering. Fast delivery. More time for in the grind.",
    items: [
      {
        id: "307-65-001",
        title: "Stone Tool",
        description: "Standard Duty",
        imageSrc: img("No Picture"),
        buyUrl: `${WEAR_PARTS_BASE}/products/coming-soon`,
      },
    ],
  },

  // ---------- 9) DISC ----------
  DISC: {
    id: "DISC",
    title: "Wear Parts & Accessories",
    subtitle: "Easy online ordering. Fast delivery. More time for in the grind.",
    items: [
      {
        id: "805-15-002",
        title: "Carbine Tool",
        description: "Extended Life",
        imageSrc: img("No Picture"),
        buyUrl: `${WEAR_PARTS_BASE}/products/coming-soon`,
      },
      {
        id: "805-15-001",
        title: "Knife Tool",
        description: "Standard Duty",
        imageSrc: img("No Picture"),
        buyUrl: `${WEAR_PARTS_BASE}/products/coming-soon`,
      },
    ],
  },

  // ---------- 10) TRIMX ----------
  TRIMX: {
    id: "TRIMX",
    title: "Wear Parts & Accessories",
    subtitle: "Easy online ordering. Fast delivery. More time for in the grind.",
    items: [
      {
        id: "806-35-009",
        title: "Saw Blade",
        description: "Standard Duty",
        imageSrc: img("No Picture"),
        buyUrl: `${WEAR_PARTS_BASE}/products/coming-soon`,
      },
    ],
  },

  // ---------- 11) STP ----------
  STP: {
    id: "STP",
    title: "Wear Parts & Accessories",
    subtitle: "Easy online ordering. Fast delivery. More time for in the grind.",
    items: [
      {
        id: "STP-4095S",
        title: "Stumper Tooth",
        description: "Standard Duty",
        imageSrc: img("https://shop.fecon.com/cdn/shop/files/stp-4095s15.png?v=1728633594"),
        buyUrl: `${WEAR_PARTS_BASE}/products/cutter-tooth-with-hardware`,
      },
    ],
  },

  // Variants exist in your mapping (STP220/240/280/3500/360) — you can add items when you have their product URLs/images.
  STP220:  { id: "STP220",  title: "Wear Parts & Accessories", subtitle: "Easy online ordering. Fast delivery. More time for in the grind.", items: [] },
  STP240:  { id: "STP240",  title: "Wear Parts & Accessories", subtitle: "Easy online ordering. Fast delivery. More time for in the grind.", items: [] },
  STP280:  { id: "STP280",  title: "Wear Parts & Accessories", subtitle: "Easy online ordering. Fast delivery. More time for in the grind.", items: [] },
  STP3500: { id: "STP3500", title: "Wear Parts & Accessories", subtitle: "Easy online ordering. Fast delivery. More time for in the grind.", items: [] },
  STP360:  { id: "STP360",  title: "Wear Parts & Accessories", subtitle: "Easy online ordering. Fast delivery. More time for in the grind.", items: [] },
};

/** Final BUY href with serialNumber appended */
export function getBuyHref(item: WearItem, serialNumber?: string): string {
  if (item.buyUrlTemplate) return withSerialTemplate(item.buyUrlTemplate, serialNumber);
  if (item.buyUrl)         return withSerialQuery(item.buyUrl, serialNumber);
  return "#";
}
